import { Component, input, output, TemplateRef, signal, ElementRef, effect, OnDestroy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { DropdownItem } from '../button/button.component';

export interface NavigationItem {
  label: string;
  id?: string;
  icon?: string;
  dropdownItems?: DropdownItem[];
  onItemClick?: (item: NavigationItem) => void | Promise<void>;
}

@Component({
  selector: 'htx-navigation-list',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './navigation-list.component.html',
  styleUrl: './navigation-list.component.scss'
})
export class NavigationListComponent implements OnDestroy {
  items = input.required<NavigationItem[]>();
  styleClass = input<string>('');
  itemTemplate = input<TemplateRef<{ $implicit: NavigationItem }> | null>(null);

  /**
   * Event emitted when a navigation item is clicked.
   * The item's onItemClick callback will be called first if provided.
   */
  itemClicked = output<NavigationItem>();

  openDropdownId = signal<string | null>(null);

  private closeTimeout: any = null;
  private closeDelay = 300;
  private isListenerAttached = false;
  private boundOnDocumentClick: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef) {
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);

    effect(() => {
      const hasAnyDropdown = this.items().some(i => i.dropdownItems?.length);
      if (hasAnyDropdown && !this.isListenerAttached) {
        document.addEventListener('click', this.boundOnDocumentClick);
        this.isListenerAttached = true;
      }
    });
  }

  async onItemClick(item: NavigationItem): Promise<void> {
    if (item.onItemClick) {
      await item.onItemClick(item);
    }
    this.itemClicked.emit(item);
  }

  onDropdownMouseEnter(item: NavigationItem): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.openDropdownId.set(item.id || item.label);
  }

  onDropdownMouseLeave(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    this.closeTimeout = setTimeout(() => {
      this.openDropdownId.set(null);
      this.closeTimeout = null;
    }, this.closeDelay);
  }

  isDropdownOpen(item: NavigationItem): boolean {
    return this.openDropdownId() === (item.id || item.label);
  }

  selectDropdownItem(dropdownItem: DropdownItem): void {
    if (!dropdownItem.disabled && dropdownItem.value?.onItemClick) {
      dropdownItem.value.onItemClick();
    }
    this.openDropdownId.set(null);
  }

  private onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.openDropdownId()) {
      this.openDropdownId.set(null);
    }
  }

  ngOnDestroy(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    if (this.isListenerAttached) {
      document.removeEventListener('click', this.boundOnDocumentClick);
    }
  }
}
